import { useEffect, useRef, useState } from "react";
import { Mic, Square, Trash2, Play, Pause, Send, Loader2, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_SECONDS = 60;

const formatTime = (s) => {
  const m = Math.floor(s / 60).toString().padStart(2, "0");
  const r = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${r}`;
};

export const VoiceRecorder = ({ name, email }) => {
  const [permission, setPermission] = useState("idle"); // idle | granted | denied
  const [recording, setRecording] = useState(false);
  const [paused, setPaused] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [bars, setBars] = useState(Array(28).fill(4));
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const tickRef = useRef(null);
  const audioRef = useRef(null);
  const chunksRef = useRef([]);

  // ---- cleanup on unmount ----
  useEffect(() => {
    return () => {
      stopAll();
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopAll = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (tickRef.current) clearInterval(tickRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      try {
        mediaRecorderRef.current.stop();
      } catch {
        /* ignore */
      }
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
  };

  const startWaveformLoop = (analyser) => {
    const buffer = new Uint8Array(analyser.fftSize);
    const loop = () => {
      analyser.getByteTimeDomainData(buffer);
      const next = Array.from({ length: 28 }, (_, i) => {
        const slice = buffer.slice(
          Math.floor((i / 28) * buffer.length),
          Math.floor(((i + 1) / 28) * buffer.length)
        );
        let sum = 0;
        for (const v of slice) sum += Math.abs(v - 128);
        const avg = sum / slice.length;
        return Math.max(4, Math.min(48, avg * 0.9));
      });
      setBars(next);
      rafRef.current = requestAnimationFrame(loop);
    };
    loop();
  };

  const startRecording = async () => {
    setSent(false);
    setAudioBlob(null);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      setPermission("granted");

      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;
      startWaveformLoop(analyser);

      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "";
      const recorder = mime
        ? new MediaRecorder(stream, { mimeType: mime })
        : new MediaRecorder(stream);

      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blobType = recorder.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type: blobType });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        setBars(Array(28).fill(4));
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
      };

      recorder.start();
      setRecording(true);
      setPaused(false);
      setElapsed(0);

      tickRef.current = setInterval(() => {
        setElapsed((e) => {
          if (e + 1 >= MAX_SECONDS) {
            stopRecording();
            return MAX_SECONDS;
          }
          return e + 1;
        });
      }, 1000);
    } catch (err) {
      setPermission("denied");
      toast.error(
        "Microphone permission denied. Enable mic access in your browser settings to record."
      );
    }
  };

  const stopRecording = () => {
    if (tickRef.current) clearInterval(tickRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
    setPaused(false);
  };

  const discard = () => {
    setAudioBlob(null);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
    setElapsed(0);
    setSent(false);
    if (audioRef.current) audioRef.current.pause();
    setPlaying(false);
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      audioRef.current.play();
      setPlaying(true);
    }
  };

  const sendVoiceMessage = async () => {
    if (!audioBlob) return;
    setSending(true);
    try {
      const fd = new FormData();
      const ext = (audioBlob.type.includes("webm") && "webm") || "ogg";
      fd.append("audio", audioBlob, `voice-message.${ext}`);
      if (name) fd.append("name", name);
      if (email) fd.append("email", email);
      const res = await axios.post(`${API}/voice-message`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.status === 201) {
        setSent(true);
        toast.success("Voice message sent — Curtis will get back to you soon.");
        discard();
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Couldn't send your voice message. Try again or call us directly."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="voice-recorder"
      className="rm-voice-card relative overflow-hidden border border-zinc-800 bg-zinc-950/70 p-5"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="rm-overline text-violet-400">Voice message</span>
          {recording && (
            <span className="flex items-center gap-1.5 text-xs font-mono text-rose-400">
              <span className="h-1.5 w-1.5 rounded-full bg-rose-400 animate-pulse" />
              REC
            </span>
          )}
        </div>
        <span className="font-mono text-xs text-zinc-500">
          {formatTime(elapsed)} / {formatTime(MAX_SECONDS)}
        </span>
      </div>

      {/* Waveform / placeholder */}
      <div className="rm-voice-stage relative h-24 flex items-center justify-center mb-4 overflow-hidden">
        {(recording || audioBlob) && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="rm-ripple" />
            <div className="rm-ripple rm-ripple-2" />
          </div>
        )}
        <div className="relative z-10 flex items-end gap-[3px] h-16">
          {bars.map((h, i) => (
            <span
              key={i}
              className={`block w-[5px] rounded-sm transition-[height] duration-75 ${
                recording ? "bg-violet-400" : "bg-zinc-700"
              }`}
              style={{ height: `${h}px` }}
            />
          ))}
        </div>
        {!recording && !audioBlob && (
          <div className="absolute inset-0 flex items-center justify-center text-xs font-mono text-zinc-500 pointer-events-none">
            Tap the mic to start a {MAX_SECONDS}-second message
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3 flex-wrap">
        {!recording && !audioBlob && (
          <button
            type="button"
            onClick={startRecording}
            data-testid="voice-start"
            className="rm-voice-mic group"
            aria-label="Start recording"
          >
            <span className="rm-voice-mic-pulse" aria-hidden="true" />
            <Mic className="h-5 w-5 relative z-10" />
            <span className="relative z-10 ml-2 text-sm font-medium">
              Record
            </span>
          </button>
        )}

        {recording && (
          <button
            type="button"
            onClick={stopRecording}
            data-testid="voice-stop"
            className="rm-voice-mic rm-voice-mic-stop"
            aria-label="Stop recording"
          >
            <Square className="h-4 w-4 relative z-10" fill="currentColor" />
            <span className="relative z-10 ml-2 text-sm font-medium">Stop</span>
          </button>
        )}

        {audioBlob && !sending && (
          <>
            <button
              type="button"
              onClick={togglePlay}
              data-testid="voice-play"
              className="rm-btn-secondary text-sm"
              aria-label={playing ? "Pause preview" : "Play preview"}
            >
              {playing ? (
                <Pause className="h-4 w-4" />
              ) : (
                <Play className="h-4 w-4" />
              )}
              {playing ? "Pause" : "Preview"}
            </button>
            <button
              type="button"
              onClick={discard}
              data-testid="voice-discard"
              className="rm-btn-secondary text-sm"
              aria-label="Discard recording"
            >
              <Trash2 className="h-4 w-4" /> Re-record
            </button>
            <button
              type="button"
              onClick={sendVoiceMessage}
              data-testid="voice-send"
              className="rm-btn-primary text-sm"
              aria-label="Send voice message"
            >
              <Send className="h-4 w-4" /> Send
            </button>
          </>
        )}

        {sending && (
          <span className="inline-flex items-center gap-2 text-sm font-mono text-zinc-300">
            <Loader2 className="h-4 w-4 animate-spin text-violet-400" /> sending…
          </span>
        )}

        {sent && (
          <span className="inline-flex items-center gap-2 text-sm font-mono text-emerald-400">
            <CheckCircle2 className="h-4 w-4" /> sent
          </span>
        )}
      </div>

      <p className="mt-4 text-xs text-zinc-500 leading-relaxed">
        Your voice message will be sent directly to{" "}
        <span className="text-violet-300">curtis@revmatrix.ca</span>. Records up
        to {MAX_SECONDS} seconds — preview before sending.
      </p>

      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          onEnded={() => setPlaying(false)}
          className="hidden"
        />
      )}

      {permission === "denied" && (
        <p className="mt-2 text-xs text-rose-400">
          Microphone access was blocked. Update your browser settings to allow
          recording.
        </p>
      )}
    </div>
  );
};
